<template>
  <div class="full-sidebar">
    Typography
    <div class="sidebar-heading">{{ schemeName }}</div>
    <div class="first-section">
      <div class="group-body">
        <div class="form-group">
          <label class="label">Font</label>
          <input
            :style="{ fontFamily: fontFamily }"
            class="form-control non-adjustable"
            v-model="fontFamily"
            disabled
          />
        </div>
        <div class="form-group">
          <label class="label">Text size </label>
          <div class="size-form-row">
            <div class="color-container">
              <input class="form-control non-adjustable" v-model="fontSize" disabled />
            </div>
            <div class="form-group open-dropdown-container">
              <button
                :class="{ 'dropdown-active': showDropdown }"
                class="material-icons-outlined align-middle dropdown-button"
                @click="toggleDropdown"
              >
                format_line_spacing
              </button>
            </div>
          </div>
          <div v-if="showDropdown" class="dropdown-menu-right dropdown">
            <div class="form-group">
              <label for="lineHeight" class="label">Line height </label>
              <input class="form-control" v-model="lineHeight" disabled />
            </div>
            <div class="form-group">
              <label class="label">Letter spacing</label>
              <input class="form-control" v-model="letterSpacing" disabled />
            </div>
          </div>
        </div>
        <div class="form-group">
          <div class="form-row">
            <div class="font-style-container">
              <label for="label">Font style</label>
              <select
                class="form-control non-adjustable select"
                id="style"
                v-model="fontStyle"
                disabled
              >
                <option
                  class="select-options"
                  v-for="style in styles"
                  :key="style.value"
                  :value="style.value"
                >
                  {{ style.label }}
                </option>
              </select>
            </div>
            <div class="color-picker-container">
              <label class="label">Color</label>
              <ColorPicker disabled class="color-picker-comp" v-model="fontColor" />
            </div>
          </div>
        </div>
        <div class="form-group">
          <label class="label">Alignment</label>
          <div class="alignment-group">
            <div v-for="alignment in alignments" :key="alignment.label">
              <div
                :class="{ 'selected-alignment': alignment.value === fontAlignment }"
                class="align-button"
                :value="alignment.value"
                :id="alignment.label"
                disabled
              >
                <i class="material-icons-outlined align-middle">
                  {{ `format_align_${alignment.value}` }}</i
                >
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="button-container">
      <div class="top-button-row">
        <button type="button" class="button-2-secondary" @click="closeSidebar">Cancel</button>
        <button type="button" class="button-2-secondary" @click="openDeleteSchemeModal(scheme)">
          <i class="material-icons-outlined align-middle">delete_outline</i>
          Delete
        </button>
      </div>
      <DeleteSchemeModal
        v-if="showDeleteSchemeModal"
        @close="showDeleteSchemeModal = false"
        :scheme="selectedScheme"
        @deleted="handleSchemeDeleted"
      />
    </div>
  </div>
</template>

<script>
  import DeleteSchemeModal from '@/components/admin/OrganisationDetails/Settings/DeleteSchemeModal.vue';
  import NumberInput from '@/components/common/toolbar/NumberInput.vue';
  import FontSelector from '@/components/common/fontSelector/FontSelector.vue';
  import ColorPicker from '@/components/ColorPicker.vue';

  export default {
    name: 'TypographySidebar',

    components: {
      DeleteSchemeModal,
      NumberInput,
      FontSelector,
      ColorPicker,
    },

    props: {
      scheme: {
        type: Object,
        required: true,
      },
    },

    data() {
      return {
        isSaving: false,
        selectedScheme: null,
        schemeName: '',
        fontFamily: '',
        fontSize: 0,
        fontColor: '',
        lineHeight: 0,
        letterSpacing: 0,
        fontStyle: 'normal',
        fontAlignment: 'left',
        showDeleteSchemeModal: false,
        styles: [
          { value: 'normal', label: 'Normal' },
          { value: 'italic', label: 'Italic' },
        ],
        showDropdown: false,
        alignments: [
          { value: 'left', label: 'Left' },
          { value: 'center', label: 'Center' },
          { value: 'right', label: 'Right' },
        ],
      };
    },
    mounted() {
      this.selectedScheme = this.scheme;
      this.schemeName = this.scheme.name;
      this.fontFamily = this.scheme.config['font-family'];
      this.fontSize = this.scheme.config['font-size'] || 16;
      this.fontColor = this.scheme.config['font-color'];
      this.lineHeight = this.scheme.config['line-height'] || 1.5;
      this.letterSpacing = this.scheme.config['letter-spacing'] || 0;
      this.fontStyle = this.scheme.config['font-style'] || 'normal';
      this.fontAlignment = this.scheme.config['text-alignment'] || 'left';
    },
    watch: {
      scheme() {
        this.selectedScheme = this.scheme;
        this.schemeName = this.scheme.name;
        this.fontFamily = this.scheme.config['font-family'];
        this.fontSize = this.scheme.config['font-size'];
        this.fontColor = this.scheme.config['font-color'] || '#000000';
        this.lineHeight = this.scheme.config['line-height'] || 1.5;
        this.letterSpacing = this.scheme.config['letter-spacing'] || 0;
        this.fontStyle = this.scheme.config['font-style'] || 'normal';
      },
    },
    computed: {},
    methods: {
      openDeleteSchemeModal(scheme) {
        this.selectedScheme = scheme;
        this.showDeleteSchemeModal = true;
      },
      closeSidebar() {
        this.$emit('close');
      },
      handleSchemeDeleted() {
        this.$emit('update');
      },
      toggleDropdown() {
        this.showDropdown = !this.showDropdown;
      },
    },
  };
</script>

<style scoped lang="scss">
  .full-sidebar {
    overflow: hidden;
    position: relative;
    height: 100%;
    background-color: #fff;
    width: 20vw;
    max-width: 20vw;
    min-width: 20vw;
    font-size: 14px;
    padding: 20px;
    border-top: solid 2px #f0f0f0;
    display: flex;
    flex-direction: column;
  }

  .group-body {
    display: grid;
    gap: 16px;
  }
  .form-group {
    margin-bottom: 0px;
  }

  .import-link {
    font-size: 12px;
    line-height: 24px;
    letter-spacing: 0%;
    color: #151515;
    cursor: pointer;
  }

  .first-section {
    display: flex;
    flex-direction: column;
    height: 100%;
    overflow: auto;
  }

  .sidebar-heading {
    font-family: Poppins;
    font-weight: 500;
    font-size: 16px;
    line-height: 24px;
    letter-spacing: 0%;
    margin-bottom: 12px;
  }

  label {
    width: 100%;
    margin-bottom: 0px;
  }

  .label-header {
    display: flex;
    justify-content: space-between;
  }

  .button-container {
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding-top: 10px;
    background-color: #ffffff;
    position: sticky;
  }

  button {
    min-width: 40px;
  }

  .top-button-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px;
  }

  #fontFamily {
    display: flex;
    justify-content: space-between;
    align-items: center;
    cursor: pointer;
  }

  .form-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    margin: 0px;
    gap: 10px;
  }

  .size-form-row {
    display: grid;
    grid-template-columns: 1fr auto auto;
  }

  .select-box {
    width: 95%;
    padding: 6px 0px;
    border: none;
    font-size: 16px;
    color: #151515;
    height: auto;
    background-color: #fff;
  }

  .dropdown-menu-right {
    border: solid 1px #f0f0f0;
    position: absolute;
    will-change: transform;
    padding: 10px;
    z-index: 1000;
    background-color: #fff;
    margin-right: 5px;
    margin-top: -5px;
    width: 130px;
  }

  .open-dropdown-container {
    width: 100%;
    display: flex;
    align-items: end;
    justify-content: center;
    flex-direction: column;
    margin: 0px;
  }

  .dropdown-button {
    background-color: #fff;
    border: none;
    cursor: pointer;
    font-size: 24x;
    color: #212529;
  }

  .dropdown-active {
    color: $primaryRed;
  }

  .dropdown-button:hover {
    color: $primaryRed;
  }
  .select-container {
    width: 98%;
  }

  .color-picker-comp {
    .picker {
      position: absolute;
      left: -100px;
      z-index: 20;
    }
  }

  .alignment-group {
    display: flex;
    gap: 14px;
    padding-top: 6px;
  }

  .alignment-container {
    display: flex;
    gap: 14px;
    margin-top: 8px;
  }

  .align-button {
    background-color: #fff;
    border: none;
    font-size: 24x;
    padding: 0px;
    padding: 6px;
    color: #6a6b6a;
    border-radius: 5px;
    border: solid 1px transparent;
  }

  .selected-alignment {
    border: solid 1px #f5f5f5;
    border-radius: 5px;
    background: #f5f5f5;
    color: #000;
  }

  .non-adjustable {
    color: #000000;
  }

  .select {
    background-color: #ffffff;
    border: none;
  }
</style>
